import { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import 'yup-phone-lite';
import * as Yup from 'yup';
import {
  APPROVAL_STATUS,
  CUSTOMER_STATUSES,
  CUSTOMER_TYPE,
  EXTERNAL_AML_LINK,
  AML_PROVIDERS,
  ONBOARDING_STATUS,
  PASS_OR_FAIL,
  PERSON_TITLE,
  POP_UP_TYPE,
  RISK_PROFILE,
  YES_OR_NO
} from 'components/constants';
import countries from 'components/countries';
import i18nContext from 'components/i18n-context';
import OperatorsAsyncSelect from 'components/OperatorsAsyncSelect';
import { PopConfirmationScheme } from 'components/PopUpScheme/PopConfirmationScheme';
import { useApplicationContext } from 'contexts/ApplicationContext';
import { processDetailsErrorResponse } from 'services/notificationUtils';
import {
  ACCOUNT_STATUS_UPDATE,
  CORPORATE_ACCOUNT_DETAILS_UPDATE,
  FULL_ACCESS,
  hasRole,
  INDIVIDUAL_ACCOUNT_DETAILS_UPDATE
} from 'services/roles';
import {
  convertDate,
  formatDateTime,
  countryCodeRegExp,
  emailRegExp,
  normalizePhone,
  parseExternalAmlState,
  regularFieldRegExp
} from 'services/utils';
import Button from 'uikit/Button/Button';
import { CustomDatePicker } from 'uikit/CustomDatePicker';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import { PopUp } from 'uikit/PopUp/PopUp';
import './CustomerDetailsTab.scss';

// Maps nested server field names (from FieldError.fields) to the corresponding formik field names
const SERVER_DETAILS_FIELD_NAME_MAP = {
  'details.birthday': 'birthday',
  'details.passport_number': 'passportNumber',
  'details.passport_issue_country': 'passportCountry',
  'details.passport_issue_date': 'passportDate',
  'details.passport_expiry_date': 'passportExpiryDate',
  'details.position': 'position',
  'details.title': 'title',
  'details.address.full_address': 'fullAddress',
  'details.address.address_1': 'address1',
  'details.address.address_2': 'address2',
  'details.address.city': 'city',
  'details.address.country': 'country',
  'details.address.state': 'state',
  'details.address.zip_code': 'zipCode'
};

const popUpInitialData = {
  isShowPopUp: false,
  popUpType: '',
  title: '',
  description: '',
  confirmButtonLabel: '',
  cancelButtonLabel: '',
  onCancel: () => {},
  onConfirm: () => {}
};

const CustomerDetailsTab = ({
  customerDetails,
  fieldsErrors: rawFieldsErrors,
  userData,
  handleUpdateCustomerDetails,
  onChangeCustomerStatus,
  onChangeOnboardingStatus,
  onSaveEmail,
  onSaveAmlStatus,
  onChangeExternalAmlReference,
  onSaveRiskProfile,
  onSaveKycProfile,
  onSaveMlroApproval,
  onSaveDirectorApproval,
  onSaveNextAMLCheckDate,
  onSaveResponseDueDate,
  onReceiveAmlData,
  parseFullAddress
}) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();

  // Translate server-shaped field errors (e.g. "details.passport_issue_date") into
  // formik field names with a readable message, so they can highlight the right input.
  const fieldsErrors = useMemo(() => {
    if (!rawFieldsErrors || Object.keys(rawFieldsErrors).length === 0) return {};

    return Object.entries(rawFieldsErrors).reduce((acc, [serverField, code]) => {
      const formField = SERVER_DETAILS_FIELD_NAME_MAP[serverField] || serverField;
      acc[formField] = processDetailsErrorResponse({ [serverField]: code });
      return acc;
    }, {});
  }, [rawFieldsErrors]);

  const [customerStatus, setCustomerStatus] = useState(customerDetails?.status || '');
  const [onboardingStatus, setOnboardingStatus] = useState(customerDetails?.onboardingStatus || '');
  const [email, setEmail] = useState(customerDetails?.email || '');
  const [amlExternalReference, setAmlExternalReference] = useState(customerDetails?.amlExternalReference || '');
  const [amlStatus, setAmlStatus] = useState(customerDetails?.amlStatus || '');
  const [riskProfile, setRiskProfile] = useState(customerDetails?.risk_profile || '');
  const [kycProfile, setKycProfile] = useState(customerDetails?.kycProfile || '');
  const [nextAMLCheckDate, setNextAMLCheckDate] = useState(userData?.nextAMLCheckDate || '');
  const [responseDueDate, setResponseDueDate] = useState(
    customerDetails?.customerResponseDeadline ? new Date(customerDetails?.customerResponseDeadline) : ''
  );
  const [mlroApproval, setMlroApproval] = useState(customerDetails?.mlro_approval || '');
  const [directorApproval, setDirectorApproval] = useState(customerDetails?.director_approval || '');
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  const customerStatusOptions =
    constants?.accountStatuses?.map((value) => ({
      key: value,
      value: i18n.getMessage(`customers.status.${value}`)
    })) || [];

  const onboardingStatusOptions = Object.keys(ONBOARDING_STATUS).map((status) => {
    return {
      key: ONBOARDING_STATUS[status],
      value: i18n.getMessage('onboardingStatus.' + ONBOARDING_STATUS[status])
    };
  });

  const titleOptions = Object.values(PERSON_TITLE).map((value) => ({
    key: value,
    value: i18n.getMessage(`personTitle.${value}`)
  }));

  const countriesOptions = Object.values(countries).map((value) => ({
    key: value,
    value: i18n.getMessage(value)
  }));

  const amlStatusOptions = constants?.amlStatuses?.map((value) => ({
    key: value,
    value: i18n.getMessage(`aml.status.${value}`)
  }));

  const riskProfileOptions = Object.values(RISK_PROFILE).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.riskProfile.${value}`)
  }));

  // Only profiles of this customer's type are selectable; the backend rejects a profile of another type
  const kycProfileOptions =
    constants?.kycProfiles
      ?.filter(({ accountType }) => accountType === CUSTOMER_TYPE.INDIVIDUAL)
      .map(({ kycProfile: profile }) => ({
        key: profile,
        value: i18n.getMessage(`customers.kycProfile.${profile}`)
      })) || [];

  const yesOrNoOptions = Object.values(YES_OR_NO).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.yesOrNoSelect.${value}`)
  }));

  const passOrFailOptions = Object.values(PASS_OR_FAIL).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.passOrFailSelect.${value}`)
  }));

  const approvalOptions = Object.values(APPROVAL_STATUS).map((value) => ({
    key: value,
    value: i18n.getMessage(`customers.approvalStatus.${value}`)
  }));

  const requiredFieldErrorMessage = i18n.getMessage('customer.validation.isRequired');
  const invalidFormatErrorMessage = i18n.getMessage('customer.validation.invalidFormat');
  const invalidCountryCodeErrorMessage = i18n.getMessage('customer.validation.invalidCountryCode');
  const expiryDateBeforeIssueDateErrorMessage = i18n.getMessage('customer.validation.expiryDateBeforeIssueDate');

  const validationSchema = Yup.object().shape({
    source: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(250, i18n.getMessage('customer.validation.maxSize', { size: 250 })),
    name: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
      .trim(),
    surname: Yup.string()
      .required(requiredFieldErrorMessage)
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
      .trim(),
    birthday: Yup.date().nullable(),
    passportNumber: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(30, i18n.getMessage('customer.validation.maxSize', { size: 30 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(30, i18n.getMessage('customer.validation.maxSize', { size: 30 }))
    }),
    passportCountry: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .length(2)
          .matches(countryCodeRegExp, invalidCountryCodeErrorMessage)
          .uppercase(),
      otherwise: (schema) =>
        schema.nullable().length(2).matches(countryCodeRegExp, invalidCountryCodeErrorMessage).uppercase()
    }),
    passportDate: Yup.date().nullable(),
    passportExpiryDate: Yup.date()
      .nullable()
      .test('is-after-issue-date', expiryDateBeforeIssueDateErrorMessage, function (value) {
        const { passportDate } = this.parent;
        if (!value || !passportDate) return true;
        return new Date(value).getTime() > new Date(passportDate).getTime();
      })
      .when('customerStatus', {
        is: CUSTOMER_STATUSES.VERIFIED,
        then: (schema) => schema.required(requiredFieldErrorMessage),
        otherwise: (schema) => schema.nullable()
      }),
    fullAddress: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    address1: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(200, i18n.getMessage('customer.validation.maxSize', { size: 200 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(200, i18n.getMessage('customer.validation.maxSize', { size: 200 }))
    }),
    /*address2: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
    }),*/
    city: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
    }),
    country: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .length(2)
          .matches(countryCodeRegExp, invalidCountryCodeErrorMessage)
          .uppercase(),
      otherwise: (schema) =>
        schema.nullable().length(2).matches(countryCodeRegExp, invalidCountryCodeErrorMessage).uppercase()
    }),
    state: Yup.string()
      .nullable()
      .matches(regularFieldRegExp, invalidFormatErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 })),
    zipCode: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 })),
      otherwise: (schema) =>
        schema
          .nullable()
          .matches(regularFieldRegExp, invalidFormatErrorMessage)
          .max(10, i18n.getMessage('customer.validation.maxSize', { size: 10 }))
    }),
    position: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      otherwise: (schema) => schema.nullable().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    accountOpeningPurpose: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      otherwise: (schema) => schema.nullable().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    accountClosingReason: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.CLOSED,
      then: (schema) =>
        schema
          .required(requiredFieldErrorMessage)
          .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
      otherwise: (schema) => schema.nullable().max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
    }),
    pepList: Yup.string().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage).oneOf(['PASS', 'FAIL']),
      otherwise: (schema) => schema.nullable()
    }),
    nextAMLCheckDate: Yup.date().nullable(),
    customerResponseDeadline: Yup.date().nullable(),
    customerOwner: Yup.object().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage),
      otherwise: (schema) => schema.nullable()
    }),
    customerManager: Yup.object().when('customerStatus', {
      is: CUSTOMER_STATUSES.VERIFIED,
      then: (schema) => schema.required(requiredFieldErrorMessage),
      otherwise: (schema) => schema.nullable()
    }),
    agentName: Yup.string()
      .nullable()
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
    contactPhone: Yup.string()
      .nullable()
      .required(requiredFieldErrorMessage)
      .max(25, i18n.getMessage('customer.validation.maxSize', { size: 25 }))
      .matches(/^\+/, i18n.getMessage('validation.invalidPhone'))
      .phone(undefined, i18n.getMessage('validation.invalidPhone')),
    contactEmail: Yup.string()
      .nullable()
      .required(requiredFieldErrorMessage)
      .max(50, i18n.getMessage('customer.validation.maxSize', { size: 50 }))
      .trim()
      .matches(emailRegExp, { message: i18n.getMessage('customer.validation.invalidEmail') }),
    googleDriveLink: Yup.string()
      .nullable()
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 })),
    internalAmlDocumentsLink: Yup.string()
      .nullable()
      .max(255, i18n.getMessage('customer.validation.maxSize', { size: 255 }))
  });

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      customerStatus: customerDetails?.status || '',
      userData: userData,
      source: customerDetails.source ? customerDetails.source : '',
      name: customerDetails.name,
      surname: customerDetails.surname,
      birthday: customerDetails?.details?.birthday ? new Date(customerDetails.details.birthday) : null,
      passportNumber: customerDetails?.details?.passport_number,
      passportCountry: customerDetails?.details?.passport_issue_country,
      passportDate: customerDetails?.details?.passport_issue_date
        ? new Date(customerDetails.details.passport_issue_date)
        : null,
      passportExpiryDate: customerDetails?.details?.passport_expiry_date
        ? new Date(customerDetails.details.passport_expiry_date)
        : null,
      fullAddress: customerDetails?.details?.address.full_address,
      address1: customerDetails?.details?.address.address_1,
      address2: customerDetails?.details?.address.address_2,
      city: customerDetails?.details?.address.city,
      country: customerDetails?.details?.address.country,
      state: customerDetails?.details?.address.state,
      zipCode: customerDetails?.details?.address.zip_code,
      sanctionsRelated: typeof userData.sanctionsRelated === 'boolean' ? userData.sanctionsRelated : '',
      adverseMediaInvolved: typeof userData.adverseMediaInvolved === 'boolean' ? userData.adverseMediaInvolved : '',
      position: userData.position || '',
      title: userData.title || '',
      accountOpeningPurpose: userData.accountOpeningPurpose || '',
      accountClosingReason: userData.accountClosingReason || '',
      pepList: userData.pepList || '',
      customerOwner: customerDetails?.customer_owner || '',
      customerManager: customerDetails?.customer_manager || '',
      agentName: customerDetails?.agent_name || '',
      contactPhone: customerDetails.contactPhone || '',
      contactEmail: customerDetails.contactEmail || customerDetails.email || '',
      googleDriveLink: customerDetails.googleDriveLink || '',
      internalAmlDocumentsLink: customerDetails.internalAmlDocumentsLink || ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      handleUpdateCustomerDetails({
        details: {
          address: {
            full_address: values.fullAddress ? values.fullAddress.trim() : null,
            address_1: values.address1 ? values.address1.trim() : null,
            address_2: values.address2 ? values.address2.trim() : null,
            city: values.city ? values.city.trim() : null,
            country: values.country ? values.country : null,
            state: values.state ? values.state.trim() : null,
            zip_code: values.zipCode ? values.zipCode.trim() : null
          },
          birthday: values.birthday ? convertDate(values.birthday) : null,
          passport_number: values.passportNumber ? values.passportNumber.trim() : null,
          passport_issue_country: values.passportCountry ? values.passportCountry : null,
          passport_issue_date: values.passportDate ? convertDate(values.passportDate) : null,
          passport_expiry_date: values.passportExpiryDate ? convertDate(values.passportExpiryDate) : null,
          position: values.position ? values.position : null,
          title: values.title ? values.title : null
        },
        hashtags:
          customerDetails.hashtags && typeof customerDetails.hashtags === 'string'
            ? customerDetails.hashtags.split('\n')
            : [],
        name: values.name ? values.name : null,
        surname: values.surname ? values.surname : null,
        declaredAccountTurnover: customerDetails.declaredAccountTurnover
          ? customerDetails.declaredAccountTurnover
          : null,
        partners: customerDetails.partners ? customerDetails.partners : null,
        additionalComments: values.additionalComments ? values.additionalComments : null,
        commentToDocuments: values.commentDocuments ? values.commentDocuments : null,
        pepList: values.pepList ? values.pepList : null,
        source: values.source ? values.source : null,
        sanctionsRelated: values.sanctionsRelated ? values.sanctionsRelated : null,
        adverseMediaInvolved: values.adverseMediaInvolved ? values.adverseMediaInvolved : null,
        customerOwner: values.customerOwner ? values.customerOwner.id : null,
        customerManager: values.customerManager ? values.customerManager.id : null,
        agentName: values.agentName ? values.agentName : null,
        contactEmail: values.contactEmail ? values.contactEmail : null,
        contactPhone: values.contactPhone ? normalizePhone(values.contactPhone) : null,
        googleDriveLink: values.googleDriveLink ? values.googleDriveLink : null,
        internalAmlDocumentsLink: values.internalAmlDocumentsLink ? values.internalAmlDocumentsLink : null,
        accountOpeningPurpose: values.accountOpeningPurpose ? values.accountOpeningPurpose : null,
        accountClosingReason: values.accountClosingReason ? values.accountClosingReason : null
      });
    }
  });

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    setErrors,
    setValues,
    validateForm,
    resetForm,
    validateField
  } = form;

  // react-datepicker's minDate is inclusive, but expiry date must be strictly after issue date
  const minExpiryDate = values?.passportDate ? new Date(values.passportDate.getTime() + 24 * 60 * 60 * 1000) : null;

  const handleContactPhoneBlur = () => {
    setFieldValue('contactPhone', normalizePhone(values.contactPhone));
    validateField('contactPhone');
  };

  // Simple trim onBlur for name fields
  const handleNameBlur = (fieldName) => {
    const trimmedValue = values[fieldName] ? values[fieldName].trim().replace(/\s+/g, ' ') : '';
    setFieldValue(fieldName, trimmedValue);
    validateField(fieldName);
  };
  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    if (formValues) {
      setValues(formValues);
    }
    setCustomerStatus(customerDetails?.status || '');
    setFieldValue('customerStatus', customerDetails?.status || '');
    setErrors({});
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDetails?.status]);

  useEffect(() => {
    setOnboardingStatus(customerDetails?.onboardingStatus || '');
  }, [customerDetails?.onboardingStatus]);

  useEffect(() => {
    setEmail(customerDetails?.email || '');
  }, [customerDetails?.email]);

  useEffect(() => {
    setAmlExternalReference(customerDetails?.amlExternalReference || '');
  }, [customerDetails?.amlExternalReference]);

  useEffect(() => {
    setAmlStatus(customerDetails?.amlStatus || '');
  }, [customerDetails?.amlStatus]);

  useEffect(() => {
    setRiskProfile(customerDetails?.risk_profile || '');
  }, [customerDetails?.risk_profile]);

  useEffect(() => {
    setKycProfile(customerDetails?.kycProfile || '');
  }, [customerDetails?.kycProfile]);

  useEffect(() => {
    setNextAMLCheckDate(userData?.nextAMLCheckDate || '');
  }, [userData?.nextAMLCheckDate]);

  useEffect(() => {
    setResponseDueDate(
      customerDetails?.customerResponseDeadline ? new Date(customerDetails?.customerResponseDeadline) : ''
    );
  }, [customerDetails?.customerResponseDeadline]);

  useEffect(() => {
    setMlroApproval(customerDetails?.mlro_approval || '');
  }, [customerDetails?.mlro_approval]);

  useEffect(() => {
    setDirectorApproval(customerDetails?.director_approval || '');
  }, [customerDetails?.director_approval]);

  const firstCreatedAccount =
    customerDetails?.wallets && customerDetails?.wallets?.length
      ? customerDetails?.wallets.sort((a, b) => a.created_at - b.created_at)[0]
      : undefined;
  const externalAmlState = parseExternalAmlState(customerDetails.amlExternalState);

  const scrollToError = () => {
    const errorElement = document.querySelector('.error');
    if (errorElement) {
      const yOffset = -50;
      const y = errorElement.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleChangeAmlExternalReference = (e) => {
    setAmlExternalReference(e.target.value);
  };

  const handleChangeCustomerStatus = async () => {
    setFormValues(values);
    await onChangeCustomerStatus(customerStatus);
  };

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  const handleConfirmReceiveAmlData = async () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('customers.individual.customerDetailsTab.confirmReceiveAmlDataModal.title'),
      description: i18n.getMessage('customers.individual.customerDetailsTab.confirmReceiveAmlDataModal.message'),
      confirmButtonLabel: i18n.getMessage(
        'customers.individual.customerDetailsTab.confirmReceiveAmlDataModal.button.confirm'
      ),
      cancelButtonLabel: i18n.getMessage(
        'customers.individual.customerDetailsTab.confirmReceiveAmlDataModal.button.cancel'
      ),
      onConfirm: async () => {
        await onReceiveAmlData();
        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  return (
    <div className={'customer-details-table'}>
      <div style={{ position: 'relative' }}></div>
      <form
        action={'/form'}
        autoComplete={'off'}
        id={'send'}
        onSubmit={handleSubmit}
        className={'customer-details-form'}
      >
        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.customerDetails')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerStatus')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'customerStatus'}
                    name={'customerStatus'}
                    options={customerStatusOptions}
                    value={customerStatus}
                    onChange={(name, value) => setCustomerStatus(value)}
                    isDisabled={!hasRole(ACCOUNT_STATUS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={handleChangeCustomerStatus}
                    isDisabled={!hasRole(ACCOUNT_STATUS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.onboardingStatus')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'onboardingStatus'}
                    name={'onboardingStatus'}
                    options={onboardingStatusOptions}
                    value={onboardingStatus}
                    onChange={(name, value) => setOnboardingStatus(value)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onChangeOnboardingStatus(onboardingStatus)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.customerId')}</td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>{customerDetails.account_number}</span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerEmail')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <Input className={'table-cell-input'} name={'email'} value={email} onChange={handleChangeEmail} />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveEmail(email)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerRegistrationTime')}
              </td>
              <td className={'table-cell'}>
                <div className={'table-cell-span-wrapper'}>
                  <span className={'table-cell-span'}>{formatDateTime(customerDetails?.user?.registration_date)}</span>
                  <span className={'table-cell-span'}>IP: {customerDetails?.user?.registration_ip}</span>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.firstAccountOpeningDate')}
              </td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>
                  {firstCreatedAccount ? formatDateTime(firstCreatedAccount.created_at) : ''}
                </span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerCreationDate')}
              </td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>
                  {customerDetails?.createdAt ? formatDateTime(customerDetails.createdAt) : ''}
                </span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerClosureDate')}
              </td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>
                  {customerDetails?.closedAt ? formatDateTime(customerDetails.closedAt) : ''}
                </span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerOwner')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <OperatorsAsyncSelect
                    handleValueChanged={(value) => setFieldValue('customerOwner', value)}
                    value={values.customerOwner}
                    error={errors.customerOwner}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerManager')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <OperatorsAsyncSelect
                    handleValueChanged={(value) => setFieldValue('customerManager', value)}
                    value={values.customerManager}
                    error={errors.customerManager}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerSource')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'source'}
                    value={values.source}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.source || fieldsErrors?.source}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.agentName')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'agentName'}
                    value={values.agentName}
                    onChange={handleChange}
                    error={errors?.agentName || fieldsErrors?.agentName}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.customerContactDetails')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerContactEmailAddress')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'contactEmail'}
                    value={values.contactEmail}
                    onChange={handleChange}
                    error={errors?.contactEmail || fieldsErrors?.contactEmail}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.customerContactPhoneNumber')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'contactPhone'}
                    value={values.contactPhone}
                    onChange={handleChange}
                    onBlur={handleContactPhoneBlur}
                    error={errors?.contactPhone || fieldsErrors?.contactPhone}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.externalAmlProvider')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.kycProfile')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'kycProfile'}
                    name={'kycProfile'}
                    options={kycProfileOptions}
                    value={kycProfile}
                    onChange={(name, value) => setKycProfile(value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveKycProfile(kycProfile)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE) || !kycProfile}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.amlExternalReference')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'amlExternalReference'}
                    value={amlExternalReference}
                    onChange={handleChangeAmlExternalReference}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={fieldsErrors?.amlExternalReference}
                  />
                  <Button
                    className={'customer-details-tab-button aml-action-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onChangeExternalAmlReference(amlExternalReference)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.saveAndUpdateAmlData')}
                  </Button>
                  <Button
                    className={'customer-details-tab-button aml-action-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={handleConfirmReceiveAmlData}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.updateAmlData')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.externalAmlLink')}
              </td>
              <td className={'table-cell'}>
                {customerDetails?.amlVerificationProvider === AML_PROVIDERS.SUMSUB && externalAmlState.identifier && (
                  <span className={'table-cell-span'}>
                    <a
                      className={'link-normal'}
                      href={`${EXTERNAL_AML_LINK}${externalAmlState.identifier}`}
                      target={'_blank'}
                      rel={'noopener noreferrer'}
                    >
                      {`${EXTERNAL_AML_LINK}${externalAmlState.identifier}`}
                    </a>
                  </span>
                )}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.statuses')}</td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>{externalAmlState.statuses}</span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.tags')}</td>
              <td className={'table-cell'}>
                <span className={'table-cell-span'}>{externalAmlState.tags}</span>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.linkToDocuments')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'googleDriveLink'}
                    value={values.googleDriveLink}
                    onChange={handleChange}
                    error={errors?.googleDriveLink || fieldsErrors?.googleDriveLink}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.individualDetails')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.personTitle')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'title'}
                    name={'title'}
                    options={titleOptions}
                    value={values.title}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.name')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'name'}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={() => handleNameBlur('name')}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.name || fieldsErrors?.name}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.surname')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'surname'}
                    value={values.surname}
                    onChange={handleChange}
                    onBlur={() => handleNameBlur('surname')}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.surname || fieldsErrors?.surname}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.birthday')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'birthday'}
                    value={values?.birthday}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.birthday || fieldsErrors?.birthday}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.passportNumber')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'passportNumber'}
                    value={values.passportNumber}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.passportNumber || fieldsErrors?.passportNumber}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.passportIssueCountry')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDownSearch
                    className={'table-cell-input-dropdown-search'}
                    id={'passportCountry'}
                    name={'passportCountry'}
                    options={countriesOptions}
                    value={values.passportCountry}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.passportCountry || fieldsErrors?.passportCountry}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.passportIssueDate')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'passportDate'}
                    value={values?.passportDate}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.passportDate || fieldsErrors?.passportDate}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.passportExpiryDate')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'passportExpiryDate'}
                    value={values?.passportExpiryDate}
                    minDate={minExpiryDate}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.passportExpiryDate || fieldsErrors?.passportExpiryDate}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.purposeOfOpeningAnAccount')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'accountOpeningPurpose'}
                    value={values.accountOpeningPurpose}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.accountOpeningPurpose || fieldsErrors?.accountOpeningPurpose}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.accountClosingReason')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'accountClosingReason'}
                    value={values.accountClosingReason}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.accountClosingReason || fieldsErrors?.accountClosingReason}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.address')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.fullAddress')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'fullAddress'}
                    value={values.fullAddress}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.fullAddress || fieldsErrors?.fullAddress}
                  />
                  <Button
                    className={'customer-details-tab-button parse-full-address-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={async () => {
                      try {
                        const parsedAddress = await parseFullAddress(values?.fullAddress);

                        if (parsedAddress) {
                          await setFieldValue('address1', parsedAddress.address_1 || '');
                          await setFieldValue('address2', parsedAddress.address_2 || '');
                          await setFieldValue('zipCode', parsedAddress.zip_code || '');
                          await setFieldValue('city', parsedAddress.city || '');
                          await setFieldValue('country', parsedAddress.country || '');
                          await setFieldValue('state', parsedAddress.state || '');
                          await setFieldValue('fullAddress', parsedAddress.full_address || '');
                        }
                      } catch (error) {
                        // Error already handled in store
                      }
                    }}
                    isDisabled={!hasRole(FULL_ACCESS) || !values.fullAddress?.trim()}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.parseAddress')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.address1')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'address1'}
                    value={values.address1}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.address1 || fieldsErrors?.address1}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.address2')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'address2'}
                    value={values.address2}
                    onChange={handleChange}
                    isDisabled={/*!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)*/ true}
                    error={errors?.address2 || fieldsErrors?.address2}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.zipCode')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'zipCode'}
                    value={values.zipCode}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.zipCode || fieldsErrors?.zipCode}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.city')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'city'}
                    value={values.city}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.city || fieldsErrors?.city}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.state')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'state'}
                    value={values.state}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.state || fieldsErrors?.state}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.country')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDownSearch
                    className={'table-cell-input-dropdown-search'}
                    id={'country'}
                    name={'country'}
                    options={countriesOptions}
                    value={values.country}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.country || fieldsErrors?.country}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.aml')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.amlStatus')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'amlStatus'}
                    name={'amlStatus'}
                    options={amlStatusOptions}
                    value={amlStatus}
                    onChange={(name, value) => setAmlStatus(value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveAmlStatus(amlStatus)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.riskProfile')}</td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'riskProfile'}
                    name={'riskProfile'}
                    options={riskProfileOptions}
                    value={riskProfile}
                    onChange={(name, value) => setRiskProfile(value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveRiskProfile(riskProfile)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.nextAmlCheckDate')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'nextAMLCheckDate'}
                    value={nextAMLCheckDate}
                    onChange={(name, value) => setNextAMLCheckDate(value)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveNextAMLCheckDate(convertDate(nextAMLCheckDate))}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.responseDueDate')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <CustomDatePicker
                    className={'table-cell-date-input'}
                    name={'customerResponseDeadline'}
                    value={responseDueDate}
                    onChange={(name, value) => setResponseDueDate(value)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveResponseDueDate(convertDate(responseDueDate))}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.internalAmlDocumentsLink')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'internalAmlDocumentsLink'}
                    value={values.internalAmlDocumentsLink}
                    onChange={handleChange}
                    error={errors?.internalAmlDocumentsLink || fieldsErrors?.internalAmlDocumentsLink}
                    isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.isSanctionsRelated')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'sanctionsRelated'}
                    name={'sanctionsRelated'}
                    options={yesOrNoOptions}
                    value={values.sanctionsRelated}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.isAdverseMediaInvolved')}
              </td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'adverseMediaInvolved'}
                    name={'adverseMediaInvolved'}
                    options={yesOrNoOptions}
                    value={values.adverseMediaInvolved}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.pepList')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'pepList'}
                    name={'pepList'}
                    options={passOrFailOptions}
                    value={values.pepList}
                    onChange={(name, value) => setFieldValue(name, value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.pepList || fieldsErrors?.pepList}
                  />
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.mlroApproval')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'mlroApproval'}
                    name={'mlroApproval'}
                    options={approvalOptions}
                    value={mlroApproval}
                    onChange={(name, value) => setMlroApproval(value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveMlroApproval(mlroApproval)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.directorApproval')}
              </td>
              <td className={'table-cell'}>
                <div className={'input-with-save-button-wrapper'}>
                  <InputDropDown
                    className={'table-cell-input'}
                    id={'directorApproval'}
                    name={'directorApproval'}
                    options={approvalOptions}
                    value={directorApproval}
                    onChange={(name, value) => setDirectorApproval(value)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  />
                  <Button
                    className={'customer-details-tab-button'}
                    size={'small'}
                    type={'primary'}
                    onClick={() => onSaveDirectorApproval(directorApproval)}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                  >
                    {i18n.getMessage('customers.individual.customerDetailsTab.button.save')}
                  </Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={'table-wrapper'}>
          <tbody className={'table-body'}>
            <tr className={'table-row'}>
              <td colSpan={'2'} className={'table-cell subsection'}>
                {i18n.getMessage('customers.individual.customerDetailsTab.subsection.relatedCompany')}
              </td>
            </tr>
            <tr className={'table-row'}>
              <td className={'table-cell'}>{i18n.getMessage('customers.individual.customerDetailsTab.position')}</td>
              <td className={'table-cell'}>
                <div className={'customer-details-input-wrapper'}>
                  <Input
                    className={'table-cell-input'}
                    name={'position'}
                    value={values.position}
                    onChange={handleChange}
                    isDisabled={!hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE)}
                    error={errors?.position || fieldsErrors?.position}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {hasRole(INDIVIDUAL_ACCOUNT_DETAILS_UPDATE) && (
          <div className={'bottom-actions-wrapper'}>
            <div className={'changes-actions-wrapper'}>
              <Button className={'customers-button'} size={'medium'} type={'outline'} onClick={resetForm}>
                {i18n.getMessage('customers.individual.customerDetailsTab.button.discard')}
              </Button>
              <Button
                className={'customers-button'}
                roleType={'submit'}
                size={'medium'}
                type={'primary'}
                onClick={() => {
                  validateForm(values).then(() => {
                    scrollToError();
                  });
                }}
              >
                {i18n.getMessage('customers.individual.customerDetailsTab.button.saveChanges')}
              </Button>
            </div>
          </div>
        )}
      </form>

      <PopUp alignOnCenter show={popUpData.isShowPopUp}>
        <PopConfirmationScheme
          title={popUpData.title}
          description={popUpData.description}
          onConfirmButtonTitle={popUpData.confirmButtonLabel}
          onCloseButtonTitle={popUpData.cancelButtonLabel}
          onConfirm={popUpData.onConfirm}
          onClose={popUpData.onCancel}
        />
      </PopUp>
    </div>
  );
};

CustomerDetailsTab.propTypes = {
  customerDetails: PropTypes.object.isRequired,
  userData: PropTypes.object.isRequired,
  handleUpdateCustomerDetails: PropTypes.func,
  applicationConstants: PropTypes.object,
  fieldsErrors: PropTypes.any,
  onChangeCustomerStatus: PropTypes.func,
  onChangeOnboardingStatus: PropTypes.func,
  onChangeExternalAmlReference: PropTypes.func,
  onReceiveAmlData: PropTypes.func.isRequired,
  onSaveAmlStatus: PropTypes.func.isRequired,
  onSaveNextAMLCheckDate: PropTypes.func.isRequired,
  onSaveResponseDueDate: PropTypes.func.isRequired,
  onSaveMlroApproval: PropTypes.func.isRequired,
  onSaveDirectorApproval: PropTypes.func.isRequired,
  onSaveRiskProfile: PropTypes.func.isRequired,
  onSaveKycProfile: PropTypes.func.isRequired,
  onSaveEmail: PropTypes.func.isRequired,
  parseFullAddress: PropTypes.func.isRequired
};

export default CustomerDetailsTab;
